import { useState } from 'react';
import axios from 'axios';
import { Building2, Plus, Trash2, UploadCloud, CheckCircle2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${API_URL}/api`, timeout: 30000 });

const CATEGORIES = ['Salud', 'Gastronomia', 'Moda', 'Tecnologia', 'Educacion', 'Hogar', 'Belleza', 'Automotriz', 'Entretenimiento', 'Otro'];

const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

// Branded file-upload pill. Uploads to the Cloudinary stub, stores returned URL.
function FileField({ label, testid, value, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const b64 = await toBase64(file);
      const { data } = await publicApi.post('/public/uploads', { file_base64: b64, filename: file.name });
      onUploaded(data.url);
    } catch {
      setErr('No se pudo subir');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="b2b-file">
      <span className="b2b-file-label">{label}</span>
      <label className={`b2b-file-btn ${value ? 'done' : ''}`}>
        {busy ? <Loader2 size={15} className="spin" /> : value ? <CheckCircle2 size={15} /> : <UploadCloud size={15} />}
        {busy ? 'Subiendo…' : value ? 'Cargado' : 'Seleccionar archivo'}
        <input data-testid={testid} type="file" hidden onChange={handle} accept="image/*,application/pdf" />
      </label>
      {err && <span className="b2b-file-err">{err}</span>}
    </div>
  );
}

export default function PublicAllyRegisterPage() {
  const [form, setForm] = useState({
    company_name: '', rif: '', phone: '', address: '', category: 'Salud',
    mercantile_registry_url: '', logo_url: '',
  });
  const [reps, setReps] = useState([
    { role: 'SHAREHOLDER', full_name: '', identity_card_url: '', rif_url: '' },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const updRep = (i, k, v) => setReps(prev => prev.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRep = () => setReps(prev => [...prev, { role: 'MANAGER', full_name: '', identity_card_url: '', rif_url: '' }]);
  const removeRep = (i) => setReps(prev => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.company_name.trim() || !form.rif.trim()) {
      setError('Razon social y RIF son obligatorios');
      return;
    }
    const validReps = reps.filter(r => r.full_name.trim());
    if (validReps.length === 0) {
      setError('Agrega al menos un representante con nombre');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await publicApi.post('/public/allies/register', { ...form, representatives: validReps });
      setDone(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo enviar la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="b2b-public-wrap">
        <div className="b2b-public-card b2b-success" data-testid="ally-register-success">
          <CheckCircle2 size={56} color="#22c55e" />
          <h1>¡Solicitud recibida!</h1>
          <p>Gracias, <strong>{done.company_name}</strong>. Tu registro fue enviado al equipo KOMPA y está en revisión.</p>
          <p className="b2b-muted">Te contactaremos una vez sea aprobado. Estado actual: <strong>{done.status}</strong></p>
          <button className="b2b-btn" data-testid="register-another" onClick={() => { setDone(null); setForm({ company_name: '', rif: '', phone: '', address: '', category: 'Salud', mercantile_registry_url: '', logo_url: '' }); setReps([{ role: 'SHAREHOLDER', full_name: '', identity_card_url: '', rif_url: '' }]); }}>
            Registrar otro aliado
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="b2b-public-wrap">
      <div className="b2b-public-card" data-testid="ally-register-page">
        <div className="b2b-public-head">
          <div className="b2b-brand">KOMPA<span>.</span></div>
          <span className="b2b-pill">Aliados Comerciales</span>
        </div>
        <h1><Building2 size={26} /> Únete a la red de descuentos KOMPA</h1>
        <p className="b2b-muted">Completa los datos de tu empresa y sube los documentos legales. Tu solicitud será revisada por nuestro equipo.</p>

        <form onSubmit={handleSubmit} className="b2b-form">
          <h3 className="b2b-section">Datos de la empresa</h3>
          <div className="b2b-grid">
            <div className="form-group">
              <label>Razón social *</label>
              <input data-testid="company-name" value={form.company_name} onChange={upd('company_name')} required />
            </div>
            <div className="form-group">
              <label>RIF *</label>
              <input data-testid="company-rif" value={form.rif} onChange={upd('rif')} placeholder="J-12345678-9" required />
            </div>
            <div className="form-group">
              <label>Teléfono</label>
              <input data-testid="company-phone" value={form.phone} onChange={upd('phone')} />
            </div>
            <div className="form-group">
              <label>Rubro</label>
              <select data-testid="company-category" value={form.category} onChange={upd('category')}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group b2b-col-2">
              <label>Dirección</label>
              <input data-testid="company-address" value={form.address} onChange={upd('address')} />
            </div>
          </div>

          <div className="b2b-grid">
            <FileField label="Registro Mercantil" testid="upload-mercantile" value={form.mercantile_registry_url} onUploaded={(url) => setForm(f => ({ ...f, mercantile_registry_url: url }))} />
            <FileField label="Logo de la empresa" testid="upload-logo" value={form.logo_url} onUploaded={(url) => setForm(f => ({ ...f, logo_url: url }))} />
          </div>

          <div className="b2b-section-row">
            <h3 className="b2b-section">Representantes</h3>
            <button type="button" className="b2b-btn-ghost" data-testid="add-rep" onClick={addRep}><Plus size={15} /> Agregar</button>
          </div>

          {reps.map((r, i) => (
            <div key={i} className="b2b-rep" data-testid={`rep-${i}`}>
              <div className="b2b-grid">
                <div className="form-group">
                  <label>Tipo</label>
                  <select data-testid={`rep-role-${i}`} value={r.role} onChange={(e) => updRep(i, 'role', e.target.value)}>
                    <option value="SHAREHOLDER">Accionista</option>
                    <option value="MANAGER">Gerente / Administrador</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Nombre completo</label>
                  <input data-testid={`rep-name-${i}`} value={r.full_name} onChange={(e) => updRep(i, 'full_name', e.target.value)} />
                </div>
                <FileField label="Cédula de identidad" testid={`rep-ci-${i}`} value={r.identity_card_url} onUploaded={(url) => updRep(i, 'identity_card_url', url)} />
                <FileField label="RIF del representante" testid={`rep-rif-${i}`} value={r.rif_url} onUploaded={(url) => updRep(i, 'rif_url', url)} />
              </div>
              {reps.length > 1 && (
                <button type="button" className="b2b-rep-remove" data-testid={`remove-rep-${i}`} onClick={() => removeRep(i)}><Trash2 size={14} /></button>
              )}
            </div>
          ))}

          {error && <div className="form-error" data-testid="register-error" style={{ marginTop: 12 }}>{error}</div>}

          <button type="submit" className="b2b-btn b2b-btn-lg" data-testid="submit-ally-register" disabled={submitting}>
            {submitting ? 'Enviando…' : 'Enviar solicitud'}
          </button>
        </form>
      </div>
    </div>
  );
}
